import { ErreurApi } from '../../api/client.js';
import {
  MAX_ECHEANCES,
  MESSAGE_AUCUN_CHANGEMENT,
  MESSAGE_AUCUNE_ECHEANCE,
  MESSAGE_INCONNU,
  MESSAGE_LIENS_NON_OUVERTS,
  MESSAGE_RAISON_REQUISE,
  MESSAGE_RELECTURE_IMPOSSIBLE,
  MESSAGE_REMISE_MONTANT,
  MESSAGE_REMISE_MOTIF,
  MESSAGE_SANS_SELECTION,
  SUCCES,
  messageEcheances,
} from './libelles.js';
import { corpsApresBascule } from './modele.js';
import type { SourceFacturation } from './source.js';
import { centimesDe } from './supplements/CarteSupplements.js';
import type { Argent, CodeRegle, CorpsRegles, DonneesEquipe, Fournisseur, VueClient } from './types.js';

export type ActionEnAttente =
  | { genre: 'regles' }
  | { genre: 'echeancier'; personId: string; total: Argent; count: number }
  | { genre: 'facture'; personId: string }
  | { genre: 'liens'; installmentIds: readonly string[]; provider: Fournisseur };

export interface EtatEquipe {
  statut: 'sans-selection' | 'chargement' | 'pret' | 'erreur';
  donnees: DonneesEquipe | null;
  /** Règles dont la case a été basculée et pas encore enregistrée : `{ code: nouvelEtat }`. */
  brouillon: Partial<Record<CodeRegle, boolean>>;
  raison: string;
  nbEcheances: number;
  enAttente: ActionEnAttente | null;
  message: string | null;
  occupe: boolean;
  /** FACT-22 : saisie de la remise (sous sa case), et l'erreur précise de chaque champ. */
  remise?: SaisieRemise;
  erreursRemise?: { montant?: string; motif?: string };
}
export interface SaisieRemise {
  montant: string;
  motif: string;
  personId: string;
}
export interface ActionsEquipe {
  onSaisirRemise: (champ: keyof SaisieRemise, valeur: string) => void;
  onBasculerRegle: (code: CodeRegle) => void;
  onSaisirRaison: (texte: string) => void;
  onChoisirNombre: (n: number) => void;
  onDemander: (action: ActionEnAttente) => void;
  onConfirmer: () => Promise<void>;
  onAnnuler: () => void;
  /** Relit la facturation et le calendrier (après « Aucune date de signature… », écart C10). */
  onReessayer: () => Promise<void>;
}
export interface ControleurEquipe {
  etat: () => EtatEquipe;
  abonner: (ecouteur: () => void) => () => void;
  charger: () => Promise<void>;
  actions: ActionsEquipe;
}

export interface EtatClient {
  statut: 'chargement' | 'pret' | 'erreur';
  vue: VueClient | null;
  message: string | null;
}
export interface ControleurClient {
  etat: () => EtatClient;
  abonner: (ecouteur: () => void) => () => void;
  charger: () => Promise<void>;
}

/** Seule la phrase française du contrat est montrée : le détail du serveur (qui pourrait nommer le mandant) ne l'est jamais. */
const phrase = (erreur: unknown): string => (erreur instanceof ErreurApi ? erreur.messageUtilisateur : MESSAGE_INCONNU);

function abonnements(): { abonner: (e: () => void) => () => void; notifier: () => void } {
  const ecouteurs = new Set<() => void>();
  return {
    abonner: (ecouteur) => {
      ecouteurs.add(ecouteur);
      return () => {
        ecouteurs.delete(ecouteur);
      };
    },
    notifier: () => {
      for (const e of ecouteurs) e();
    },
  };
}

const REMISE_VIDE: SaisieRemise = { montant: '', motif: '', personId: '' };

/** Saisie de départ : la remise en vigueur (montant, motif, héritier visé), sinon des champs vides. */
function saisieEnVigueur(donnees: DonneesEquipe | null): SaisieRemise {
  const p = donnees?.facturation?.ruleSet.items.find((i) => i.ruleCode === 'remise')?.params ?? {};
  const centimes = typeof p['amountCents'] === 'number' ? p['amountCents'] : null;
  return {
    montant: centimes === null ? '' : (centimes / 100).toFixed(2).replace('.', ','),
    motif: typeof p['motif'] === 'string' ? p['motif'] : '',
    personId: typeof p['personId'] === 'string' ? p['personId'] : '',
  };
}

/** Paramètres de la remise tels que le domaine les attend ; null si un champ est invalide (les erreurs disent lequel). */
function parametresRemise(s: SaisieRemise): { params: Record<string, unknown> | null; erreurs: NonNullable<EtatEquipe['erreursRemise']> } {
  const amountCents = centimesDe(s.montant);
  const motif = s.motif.trim();
  const erreurs: NonNullable<EtatEquipe['erreursRemise']> = {
    ...(amountCents === null ? { montant: MESSAGE_REMISE_MONTANT } : {}),
    ...(motif.length < 3 ? { motif: MESSAGE_REMISE_MOTIF } : {}),
  };
  if (amountCents === null || motif.length < 3) return { params: null, erreurs };
  return { params: { amountCents, motif, ...(s.personId ? { personId: s.personId } : {}) }, erreurs };
}

const etatContrat = (donnees: DonneesEquipe | null, code: CodeRegle): boolean =>
  donnees?.facturation?.ruleSet.items.find((i) => i.ruleCode === code)?.enabled ?? false;

/** Corps de la nouvelle version : le brouillon appliqué règle par règle au jeu en vigueur. */
function corpsDuBrouillon(
  donnees: DonneesEquipe,
  brouillon: Partial<Record<CodeRegle, boolean>>,
  raison: string,
  remise: Record<string, unknown> | null,
): CorpsRegles {
  // Dossier sans règles (écart C10) : le brouillon part d'un jeu vide, la première version ne porte que les cases cochées.
  let jeu = donnees.facturation?.ruleSet ?? { versionNo: 0, reason: '', createdAt: '', items: [] };
  let corps: CorpsRegles = { reason: raison, items: jeu.items };
  for (const code of Object.keys(brouillon) as CodeRegle[]) {
    corps = corpsApresBascule(jeu, code, raison);
    jeu = { ...jeu, items: corps.items };
  }
  // FACT-22 : une remise cochée porte le montant, le motif et l'héritier visé saisis sous sa case.
  if (remise === null) return corps;
  return { ...corps, items: corps.items.map((i) => (i.ruleCode === 'remise' && i.enabled ? { ...i, params: remise } : i)) };
}

/** Contrôleur de D08 : état, brouillon des règles, confirmation avant toute écriture, rechargement après écriture. */
export function creerControleurEquipe(source: SourceFacturation, dossierId: string | null): ControleurEquipe {
  let etat: EtatEquipe = {
    statut: dossierId ? 'chargement' : 'sans-selection',
    donnees: null,
    brouillon: {},
    raison: '',
    nbEcheances: 2,
    enAttente: null,
    message: dossierId ? null : MESSAGE_SANS_SELECTION,
    occupe: false,
    remise: REMISE_VIDE,
    erreursRemise: {},
  };
  const { abonner, notifier } = abonnements();
  const changer = (suite: Partial<EtatEquipe>): void => {
    etat = { ...etat, ...suite };
    notifier();
  };

  const charger = async (): Promise<void> => {
    if (!dossierId) {
      changer({ statut: 'sans-selection', message: MESSAGE_SANS_SELECTION });
      return;
    }
    try {
      const donnees = await source.charger(dossierId);
      changer({ statut: 'pret', donnees, message: null, remise: saisieEnVigueur(donnees), erreursRemise: {} });
    } catch (erreur) {
      changer({ statut: 'erreur', donnees: null, message: phrase(erreur) });
    }
  };

  const demander = (action: ActionEnAttente): void => {
    if (etat.statut !== 'pret' || etat.occupe) return;
    if (action.genre === 'regles') {
      const remiseCochee = etat.brouillon.remise ?? etatContrat(etat.donnees, 'remise');
      const remiseModifiee =
        remiseCochee && JSON.stringify(etat.remise ?? saisieEnVigueur(etat.donnees)) !== JSON.stringify(saisieEnVigueur(etat.donnees));
      if (Object.keys(etat.brouillon).length === 0 && !remiseModifiee)
        return changer({ enAttente: null, message: MESSAGE_AUCUN_CHANGEMENT });
      if (etat.raison.trim().length < 3) return changer({ enAttente: null, message: MESSAGE_RAISON_REQUISE });
      if (remiseCochee) {
        const { params, erreurs } = parametresRemise(etat.remise ?? REMISE_VIDE);
        if (params === null) return changer({ enAttente: null, erreursRemise: erreurs, message: null });
      }
    }
    if (action.genre === 'echeancier' && !(Number.isInteger(action.count) && action.count >= 1 && action.count <= MAX_ECHEANCES)) {
      return changer({ enAttente: null, message: messageEcheances(MAX_ECHEANCES) });
    }
    if (action.genre === 'liens' && action.installmentIds.length === 0) {
      return changer({ enAttente: null, message: MESSAGE_AUCUNE_ECHEANCE });
    }
    changer({ enAttente: action, message: null });
  };

  const executer = async (action: ActionEnAttente, donnees: DonneesEquipe, id: string): Promise<string> => {
    switch (action.genre) {
      case 'regles':
        await source.enregistrerRegles(
          id,
          corpsDuBrouillon(
            donnees,
            etat.brouillon,
            etat.raison.trim(),
            (etat.brouillon.remise ?? etatContrat(donnees, 'remise')) ? parametresRemise(etat.remise ?? REMISE_VIDE).params : null,
          ),
        );
        return SUCCES.regles;
      case 'echeancier':
        await source.creerEcheancier(id, action.personId, action.total, action.count);
        return SUCCES.echeancier;
      case 'facture':
        await source.emettreFacture(id, action.personId);
        return SUCCES.facture;
      case 'liens':
        await source.envoyerLiens(id, action.installmentIds, action.provider);
        return SUCCES.liens;
    }
  };

  const confirmer = async (): Promise<void> => {
    const action = etat.enAttente;
    // Un seul envoi à la fois : le verrou est pris avant le premier `await`, un double clic n'écrit qu'une fois.
    if (!action || etat.occupe || !dossierId || !etat.donnees) return;
    changer({ occupe: true });
    let succes: string;
    try {
      succes = await executer(action, etat.donnees, dossierId);
    } catch (erreur) {
      // Écart C16 : sans fournisseur branché, l'API répond 501 ; c'est « pas encore ouvert », jamais la phrase générique.
      const nonOuvert = action.genre === 'liens' && erreur instanceof ErreurApi && erreur.status === 501;
      changer({ occupe: false, enAttente: null, message: nonOuvert ? MESSAGE_LIENS_NON_OUVERTS : phrase(erreur) });
      return;
    }
    // Écart C17 : l'écriture a réussi ; si la relecture échoue, l'écran garde les données précédentes et annonce le succès en
    // statut, avec l'invite à recharger, au lieu de basculer en erreur avec le texte du succès dans une alerte.
    const precedentes = etat.donnees;
    let relu = true;
    try {
      const relues = await source.charger(dossierId);
      changer({ statut: 'pret', donnees: relues, message: null, remise: saisieEnVigueur(relues), erreursRemise: {} });
    } catch {
      relu = false;
      changer({ statut: 'pret', donnees: precedentes });
    }
    changer({
      occupe: false,
      enAttente: null,
      message: relu ? succes : `${succes} ${MESSAGE_RELECTURE_IMPOSSIBLE}`,
      ...(action.genre === 'regles' ? { brouillon: {}, raison: '' } : {}),
    });
  };

  return {
    etat: () => etat,
    abonner,
    charger,
    actions: {
      onBasculerRegle: (code) => {
        const suivant = !(etat.brouillon[code] ?? etatContrat(etat.donnees, code));
        const brouillon = { ...etat.brouillon };
        if (suivant === etatContrat(etat.donnees, code)) delete brouillon[code];
        else brouillon[code] = suivant;
        changer({ brouillon, message: null });
      },
      onSaisirRaison: (raison) => changer({ raison }),
      onSaisirRemise: (champ, valeur) => {
        const erreursRemise = { ...(etat.erreursRemise ?? {}) };
        if (champ === 'montant') delete erreursRemise.montant;
        if (champ === 'motif') delete erreursRemise.motif;
        changer({ remise: { ...(etat.remise ?? REMISE_VIDE), [champ]: valeur }, erreursRemise });
      },
      onChoisirNombre: (n) => {
        if (Number.isInteger(n) && n >= 1 && n <= MAX_ECHEANCES) changer({ nbEcheances: n });
      },
      onDemander: demander,
      onConfirmer: confirmer,
      onAnnuler: () => changer({ enAttente: null }),
      onReessayer: charger,
    },
  };
}

/** Contrôleur de M06 : lecture seule, aucun montant autre que ceux de la vue client. */
export function creerControleurClient(charger: () => Promise<VueClient>): ControleurClient {
  let etat: EtatClient = { statut: 'chargement', vue: null, message: null };
  const { abonner, notifier } = abonnements();
  const changer = (suite: EtatClient): void => {
    etat = suite;
    notifier();
  };
  return {
    etat: () => etat,
    abonner,
    charger: async () => {
      try {
        changer({ statut: 'pret', vue: await charger(), message: null });
      } catch (erreur) {
        changer({ statut: 'erreur', vue: null, message: phrase(erreur) });
      }
    },
  };
}
